/**
 * CacheContext.jsx
 * 
 * Context React per la gestione della cache a livello applicativo
 * Fornisce accesso ai metodi di cache e stato globale della cache
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cacheService from '@/services/cacheService';

// Creazione Context
const CacheContext = createContext(null);

/**
 * Provider del context per la cache
 */
export const CacheProvider = ({ children }) => {
  // Stati globali della cache
  const [isInitialized, setIsInitialized] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);
  const [lastCleanupTime, setLastCleanupTime] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Inizializza il servizio di cache
  useEffect(() => {
    const initCache = async () => {
      try {
        await cacheService.initCacheService();
        setIsInitialized(true);
        // Carica statistiche iniziali
        updateCacheStats();
      } catch (error) {
        console.error('Errore inizializzazione cache:', error);
      }
    };
    
    initCache();
  }, []);
  
  // Aggiorna le statistiche della cache
  const updateCacheStats = useCallback(() => {
    try {
      const stats = cacheService.getCacheStats();
      setCacheStats(stats);
      return stats;
    } catch (error) {
      console.error('Errore aggiornamento statistiche cache:', error);
      return null;
    }
  }, []);
  
  // Pulisce le cache scadute
  const cleanupCache = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const removedItems = await cacheService.cleanupExpiredCache();
      setLastCleanupTime(new Date());
      updateCacheStats();
      return removedItems;
    } catch (error) {
      console.error('Errore pulizia cache:', error);
      return 0;
    } finally {
      setIsRefreshing(false);
    }
  }, [updateCacheStats]);
  
  // Invalida tutte le cache per un dataset
  const invalidateDatasetCache = useCallback(async (datasetId) => {
    try {
      setIsRefreshing(true);
      await cacheService.invalidateDatasetCache(datasetId);
      updateCacheStats();
      return true;
    } catch (error) {
      console.error('Errore invalidazione cache dataset:', error);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [updateCacheStats]);
  
  // Salva un record in cache (wrapper del service)
  const cacheDataset = useCallback(async (datasetId, data, filters = {}) => {
    return await cacheService.cacheDataset(datasetId, data, filters);
  }, []);
  
  // Recupera un record dalla cache (wrapper del service)
  const getDatasetFromCache = useCallback(async (datasetId, filters = {}) => {
    return await cacheService.getDatasetFromCache(datasetId, filters);
  }, []);
  
  // Valore del context
  const contextValue = {
    // Stati
    isInitialized,
    cacheStats,
    lastCleanupTime,
    isRefreshing,
    
    // Metodi principali
    updateCacheStats,
    cleanupCache,
    invalidateDatasetCache,
    
    // Wrapper dei metodi del service
    cacheDataset,
    getDatasetFromCache,
    
    // Metodi originali del service
    fetchDatasetFromBackend: cacheService.fetchDatasetFromBackend
  };
  
  return (
    <CacheContext.Provider value={contextValue}>
      {children}
    </CacheContext.Provider>
  );
};

/**
 * Hook per utilizzare il context della cache
 */
export const useCache = () => {
  const context = useContext(CacheContext);
  
  if (!context) {
    throw new Error('useCache deve essere utilizzato all\'interno di un CacheProvider');
  }
  
  return context;
};

// Esporta anche il context raw per utilizzi avanzati
export default CacheContext;
